import _ from 'lodash'
import apiFlow from '@/apis/flow'
import apiQueue from '@/apis/queue'
import apiField from '@/apis/field'
import apiIntent from '@/apis/intent'
import apiGrammar from '@/apis/grammar'
import apiContact from '@/apis/contact'
import apiSchedule from '@/apis/schedule'
import apiLookupTables from '@/apis/lookup_tables'
import apiTtsDictionaries from '@/apis/tts_dictionaries'

class Arrange {
  constructor() {
    this.componentTypeCheckDuplicateName = ['api', 'code', 'input', 'gen_ai']
    this.componentTypeRemapObject = [
      'intent_mapping',
      'schedule',
      'write',
      'input',
      'start',
      'transfer',
      'chat_start',
      'chat_transfer'
    ]
    this.dataList = {
      flows: [],
      queues: [],
      fields: [],
      intents: [],
      grammars: [],
      contacts: [],
      schedules: [],
      lookupTables: [],
      ttsDictionaries: []
    }
  }

  rearrangeComponent(origin, template, options) {
    const maxOriginId = parseInt(
      this.getMinMax(this.getDrawFlowNodeId(origin.drawflow), 'max')
    )
    const minTemplateId = parseInt(
      this.getMinMax(this.getNodeId(template), 'min')
    )
    const gap = this.getGap(maxOriginId, minTemplateId)
    const newTemplate = this.rearrangeNodeId(template, gap, options)
    return newTemplate
  }

  getGap(maxOrigin, minTemplate) {
    const gap = maxOrigin - minTemplate + 1
    // const gap =
    //   maxOrigin <= minTemplate
    //     ? maxOrigin - minTemplate + 1
    //     : maxOrigin + minTemplate - 1
    return gap
  }

  rearrangeNodeId(template, gap, options) {
    let newJsonUi = {}
    for (const [key, value] of Object.entries(template)) {
      let newValue = value

      newValue.id += gap

      // input
      if (Object.keys(value.inputs).length > 0) {
        for (const input in value.inputs) {
          newValue.inputs[input].connections = value.inputs[
            input
          ].connections.map(item => ({
            ...item,
            node: `${parseInt(item.node) + gap}`
          }))
        }
      }

      // output
      if (Object.keys(value.outputs).length > 0) {
        for (const output in value.outputs) {
          newValue.outputs[output].connections = value.outputs[
            output
          ].connections.map(item => ({
            ...item,
            node: `${parseInt(item.node) + gap}`
          }))
        }
      }

      // if (!copyMode && value.data.node === 'jump')
      //   newValue.data.property.component += gap

      newJsonUi[parseInt(key) + gap] = newValue
    }

    for (const [key, value] of Object.entries(newJsonUi)) {
      if (value.data.node === 'jump') {
        if (!options || (options && options.from !== options.to)) {
          const newJumpComponent = value.data.property.component + gap
          if (newJsonUi[newJumpComponent])
            value.data.property.component = newJumpComponent
          else {
            if (value.data.property.type === 'component') {
              value.data.property.component = ''
              value.data.name = 'Jump'
            }
          }
        }
      }

      if (value.data.node === 'add_retry') {
        if (
          value.data.property.type === 'component' &&
          (!options || (options && options.from !== options.to))
        ) {
          const newJumpComponent = value.data.property.component + gap
          if (newJsonUi[newJumpComponent])
            value.data.property.component = newJumpComponent
          else {
            value.data.property.component = ''
          }
        }
      }
    }

    return newJsonUi
  }

  getDrawFlowNodeId(drawflow) {
    const keys = Object.keys(drawflow)
    const nodeIds = keys.flatMap(key => this.getNodeId(drawflow[key].data))
    return nodeIds
  }

  getNodeId(flow) {
    const nodeIds = Object.keys(flow)
    return nodeIds
  }

  getMinMax(numbers, action) {
    const nums = _.uniq(numbers.map(number => parseInt(number)))
    let num = 0
    switch (action) {
      case 'min':
        num = _.min(nums)
        break
      case 'max':
        num = _.max(nums)
      default:
        break
    }
    return num
  }

  renameComponent(origin, template) {
    const componentNames = this.getComponentNameByTypes(
      origin.drawflow,
      this.componentTypeCheckDuplicateName
    )
    const newTemplate = this.renameNode(template, componentNames)
    return newTemplate
  }

  renameNode(template, componentNames) {
    let newJsonUi = {}

    const renameNodes = []
    for (const [id, value] of Object.entries(template)) {
      const type = value.data.node
      let newValue = value
      if (this.componentTypeCheckDuplicateName.includes(type)) {
        const name = value.name
        let newName = name
        if (this.isDuplicateName(componentNames, newName)) {
          let count = 0
          do {
            count++
            newName = `${name}-${count}`
          } while (this.isDuplicateName(componentNames, newName))
          newValue.name = newName
          newValue.data.name = newName
          renameNodes.push({ old: name, new: newName })
        }
      }
      newJsonUi[id] = newValue
    }

    newJsonUi = this.renameAllNode(newJsonUi, renameNodes)

    return newJsonUi
  }

  renameAllNode(jsonUi, renameList) {
    let newJsonUi = {}

    newJsonUi = this.renameNodeByType(jsonUi, renameList, 'jump')
    newJsonUi = this.recaseComponent(newJsonUi, renameList)

    return newJsonUi
  }

  renameNodeByType(jsonUi, renameList, type) {
    let newJsonUi = {}

    for (const [id, value] of Object.entries(jsonUi)) {
      let newValue = value
      if (value.data.node === type) {
        const name = renameList.find(item => value.data.name.includes(item.old))
        if (name) {
          const newName = value.data.name.replace(name.old, name.new)
          newValue.name = newName
          newValue.data.name = newName
        }
      }
      newJsonUi[id] = newValue
    }

    return newJsonUi
  }

  recaseComponent(jsonUi, renameList) {
    let newJsonUi = {}

    newJsonUi = this.recaseCondition(jsonUi, renameList)
    newJsonUi = this.recaseObjectPropertyComponent(newJsonUi, renameList, [
      'intent_mapping',
      'loop',
      'write'
    ])

    return newJsonUi
  }

  recaseCondition(jsonUi, renameList) {
    let newJsonUi = {}

    for (const [id, value] of Object.entries(jsonUi)) {
      let newValue = value
      if (value.data.node === 'condition') {
        newValue.data.property.conditions = value.data.property.conditions.map(
          item => {
            const newItem = item
            const { condition } = item
            if (condition && condition.includes('${')) {
              const oldCondition = this.getVariableInBacket(condition)
              if (oldCondition.includes('.')) {
                const componentName = this.getComponentNameFromCondition(
                  oldCondition
                )
                const name = renameList.find(item => componentName === item.old)
                if (name) {
                  const newCondition = condition.replace(
                    oldCondition,
                    oldCondition.replace(name.old, name.new)
                  )
                  newItem.condition = newCondition
                }
              }
            }
            return newItem
          }
        )
      }
      newJsonUi[id] = newValue
    }

    return newJsonUi
  }

  recaseObjectPropertyComponent(jsonUi, renameList, types) {
    let newJsonUi = {}

    for (const [id, value] of Object.entries(jsonUi)) {
      let newValue = value
      const type = value.data.node
      if (types.includes(type)) {
        let condition = ''
        switch (type) {
          case 'intent_mapping':
            condition = value.data.property.intent_variable
            break
          case 'loop':
            condition = value.data.property.variable
            break
          case 'write':
            condition = value.data.property.value
            break
          default:
            break
        }
        if (typeof condition === 'String') {
          const oldCondition = this.getVariableInBacket(condition)
          if (oldCondition.includes('.')) {
            const componentName = this.getComponentNameFromCondition(
              oldCondition
            )
            const name = renameList.find(item => componentName === item.old)
            if (name) {
              const newCondition = condition.replace(
                oldCondition,
                oldCondition.replace(name.old, name.new)
              )
              switch (type) {
                case 'intent_mapping':
                  newValue.data.property.intent_variable = newCondition
                  break
                case 'loop':
                  newValue.data.property.variable = newCondition
                  break
                case 'write':
                  newValue.data.property.value = newCondition
                  break
                default:
                  break
              }
            }
          }
        }
      }
      newJsonUi[id] = newValue
    }

    return newJsonUi
  }

  getComponentNameByTypes(drawflow, types) {
    const componentNames = []
    const keys = Object.keys(drawflow)
    keys.forEach(key => {
      const ids = Object.keys(drawflow[key].data)
      const nodeNames = ids
        .filter(id => types.includes(drawflow[key].data[id].data.node))
        .map(id => drawflow[key].data[id].name)
      componentNames.push(...nodeNames)
    })
    return componentNames
  }

  isDuplicateName(names, name) {
    return names.includes(name)
  }

  getVariableInBacket(condition) {
    return condition
      .trim()
      .split('${')[1]
      .split('}')[0]
  }

  getComponentNameFromCondition(condition) {
    return condition.trim().split('.')[0]
  }

  adjustPosition(position, nodes) {
    const nodeArr = _.values(nodes)
    const minNode = _.minBy(nodeArr, 'pos_y')

    const gapX = position.x - minNode.pos_x
    const gapY = position.y - minNode.pos_y

    for (const [key, value] of Object.entries(nodes)) {
      value.pos_x += gapX
      value.pos_y += gapY
    }

    return nodes
  }

  checkInputOutput(nodes) {
    for (const [key, value] of Object.entries(nodes)) {
      // input
      if (Object.keys(value.inputs).length > 0) {
        for (const [keyInput, valueInput] of Object.entries(value.inputs)) {
          if (valueInput.connections && valueInput.connections.length > 0) {
            const newInputs = []
            for (var i = 0; i < valueInput.connections.length; i++) {
              if (nodes[valueInput.connections[i].node]) {
                newInputs.push(valueInput.connections[i])
              }
            }

            valueInput.connections = newInputs
          }
        }
      }

      // output
      if (Object.keys(value.outputs).length > 0) {
        for (const [keyOutput, valueOutput] of Object.entries(value.outputs)) {
          if (valueOutput.connections && valueOutput.connections.length > 0) {
            const newOutputs = []
            for (var i = 0; i < valueOutput.connections.length; i++) {
              if (nodes[valueOutput.connections[i].node]) {
                newOutputs.push(valueOutput.connections[i])
              }
            }

            valueOutput.connections = newOutputs
          }
        }
      }
    }

    return nodes
  }

  async checkUiAvailable({ tabs, ui, platform }) {
    const { newTab, newUi } = this.reorderNodeIdAndTab({ tabs, ui })
    let data = newUi.drawflow

    for (const [page, obj] of Object.entries(data)) {
      // check inputs & outputs valid
      for (const [id, value] of Object.entries(obj.data)) {
        // input
        if (Object.keys(value.inputs).length > 0) {
          for (const [keyInput, valueInput] of Object.entries(value.inputs)) {
            if (valueInput.connections && valueInput.connections.length > 0) {
              const newConnection = []
              for (var i = 0; i < valueInput.connections.length; i++) {
                if (obj.data[valueInput.connections[i].node]) {
                  if (
                    Object.keys(
                      obj.data[valueInput.connections[i].node].outputs
                    ).length > 0
                  ) {
                    const find = _.find(newConnection, {
                      node: valueInput.connections[i].node,
                      input: valueInput.connections[i].input
                    })
                    if (!find) newConnection.push(valueInput.connections[i])
                  }
                }
              }
              valueInput.connections = newConnection
            }
          }
        }

        // output
        if (Object.keys(value.outputs).length > 0) {
          for (const [keyOutput, valueOutput] of Object.entries(
            value.outputs
          )) {
            if (valueOutput.connections && valueOutput.connections.length > 0) {
              const newConnection = []
              for (var i = 0; i < valueOutput.connections.length; i++) {
                if (obj.data[valueOutput.connections[i].node]) {
                  if (
                    Object.keys(
                      obj.data[valueOutput.connections[i].node].inputs
                    ).length > 0
                  ) {
                    const find = _.find(newConnection, {
                      node: valueOutput.connections[i].node,
                      output: valueOutput.connections[i].input
                    })
                    if (!find) newConnection.push(valueOutput.connections[i])
                  }
                }
              }
              valueOutput.connections = newConnection
            }
          }
        }

        // jump
        if (
          value.data.node === 'jump' &&
          value.data.property.type === 'component'
        ) {
          if (!obj.data[value.data.property.component]) {
            value.data.property.component = ''
            value.data.name = 'Jump'
          }
        }
      }
    }

    data = await this.remapObjectOfComponent(data, platform)

    newUi.drawflow = data

    return { tabs: newTab, ui: newUi }
  }

  // start remap object of component
  async remapObjectOfComponent(data, platform) {
    let newData = data
    await this.initialDataList(platform)
    newData = this.remapObject(data)
    return newData
  }

  remapObject(data) {
    let newData = data
    for (const [page, obj] of Object.entries(data)) {
      for (const [id, value] of Object.entries(obj.data)) {
        const obj = value.data
        if (this.componentTypeRemapObject.includes(obj.node)) {
          let newProperty = obj.property
          if (['start', 'chat_start'].includes(obj.node)) {
            newProperty = this.remapObjectStart(obj.property)
          } else if (['transfer', 'chat_transfer'].includes(obj.node)) {
            newProperty = this.remapObjectTransfer(obj.property)
          } else if (obj.node === 'intent_mapping') {
            newProperty = this.remapObjectIntentMapping(obj.property)
          } else if (obj.node === 'schedule') {
            newProperty = this.remapObjectSchedule(obj.property)
            if (newProperty.schedule_name) obj.name = newProperty.schedule_name
          } else if (obj.node === 'write') {
            newProperty = this.remapObjectWrite(obj.property)
          } else if (obj.node === 'input') {
            newProperty = this.remapObjectInput(obj.property)
          }
          obj.property = newProperty
        }
      }
    }
    return newData
  }

  remapObjectStart(property) {
    let newProperty = property

    const tmp_id = []
    const tmp_name = []
    const dataList = this.dataList.ttsDictionaries
    if (dataList && dataList.length > 0) {
      if (property.tts.dictionaries && property.tts.dictionaries.length) {
        for (let index = 0; index < property.tts.dictionaries.length; index++) {
          const id =
            property.tts.dictionaries && property.tts.dictionaries.length
              ? property.tts.dictionaries[index]
              : ''
          const name =
            property.tts.dictionaries_name &&
            property.tts.dictionaries_name.length
              ? property.tts.dictionaries_name[index]
              : ''
          const data = this.getDataFromList(dataList, { id, name })
          if (data) {
            tmp_id.push(data.id)
            tmp_name.push(data.name)
          }
        }
      }
    }
    newProperty.tts.dictionaries = tmp_id
    newProperty.tts.dictionaries_name = tmp_name

    let tmp_id2 = ''
    let tmp_name2 = ''
    const dataList2 = this.dataList.grammars
    if (dataList2.length > 0) {
      const data = this.getDataFromList(dataList2, {
        id: (((property || {}).default_input || {}).asr || {}).grammar_id || '',
        name:
          (((property || {}).default_input || {}).asr || {}).grammar_name || ''
      })
      if (data) {
        tmp_id2 = data.id
        tmp_name2 = data.name
      }
    }
    if (tmp_id2) {
      newProperty.default_input.asr.grammar_id = tmp_id2
      newProperty.default_input.asr.grammar_name = tmp_name2
    }

    return newProperty
  }

  remapObjectTransfer(property) {
    let newProperty = property

    if (property.type === 'flow_ui_id') {
      let tmp_id = ''
      let tmp_name = ''
      const dataList = this.dataList.flows
      if (dataList.length > 0) {
        const data = this.getDataFromList(dataList, {
          id: property.flow_ui_id,
          name: property.flow_ui_name
        })
        if (data) {
          tmp_id = data.id
          tmp_name = data.name
        }
      }
      newProperty.flow_ui_id = tmp_id
      newProperty.flow_ui_name = tmp_name
    } else if (property.type === 'contact_id') {
      let tmp_id = ''
      let tmp_name = ''
      const dataList = this.dataList.contacts
      if (dataList.length > 0) {
        const data = this.getDataFromList(dataList, {
          id: property.contact_id,
          name: property.contact_name
        })
        if (data) {
          tmp_id = data.id
          tmp_name = data.name
          // tmp_name = `${data.name} (${data.number})`
        }
      }
      newProperty.contact_id = tmp_id
      newProperty.contact_name = tmp_name
    } else if (property.type === 'live_agent') {
      let tmp_id = ''
      let tmp_name = ''
      const dataList = this.dataList.queues
      if (dataList.length > 0) {
        const data = this.getDataFromList(dataList, {
          id: property.queue_id,
          name: property.queue_name
        })
        if (data) {
          tmp_id = data.id
          tmp_name = data.name
        }
      }
      newProperty.queue_id = tmp_id
      newProperty.queue_name = tmp_name
    }

    return newProperty
  }

  remapObjectIntentMapping(property) {
    let newProperty = property

    let newMapping
    const dataList = this.dataList.intents
    if (dataList.length > 0) {
      newMapping = property.mapping.map(item => {
        const newItem = item
        const tmp_id = []
        const tmp_name = []
        if (item.intents && item.intents.length) {
          for (let index = 0; index < item.intents.length; index++) {
            const id =
              item.intents && item.intents.length ? item.intents[index] : ''
            const name =
              item.intents_name && item.intents_name.length
                ? item.intents_name[index]
                : ''
            const data = this.getDataFromList(dataList, { id, name })
            if (data) {
              tmp_id.push(data.id)
              tmp_name.push(data.name)
            }
          }
        }
        newItem.intents = tmp_id
        newItem.intents_name = tmp_name
        return newItem
      })
    } else {
      newMapping = property.mapping.map(item => {
        const newItem = item
        newItem.intents = []
        newItem.intents_name = []
        return newItem
      })
    }
    newProperty.mapping = newMapping

    return newProperty
  }

  remapObjectSchedule(property) {
    let newProperty = property

    let tmp_id = ''
    let tmp_name = ''
    const dataList = this.dataList.schedules
    if (dataList.length > 0) {
      const data = this.getDataFromList(dataList, {
        id: property.schedule,
        name: property.schedule_name
      })
      if (data) {
        tmp_id = data.id
        tmp_name = data.name
      }
    }
    newProperty.schedule = tmp_id
    newProperty.schedule_name = tmp_name

    return newProperty
  }

  remapObjectWrite(property) {
    let newProperty = property

    const dataList = this.dataList.fields
    if (property.operation === 'clear') {
      const tmp_id = []
      const tmp_name = []
      if (dataList.length > 0) {
        if (property.fields && property.fields.length) {
          for (let index = 0; index < property.fields.length; index++) {
            const id =
              property.fields && property.fields.length
                ? property.fields[index]
                : ''
            const name =
              property.fields_name && property.fields_name.length
                ? property.fields_name[index]
                : ''
            const data = this.getDataFromList(dataList, { id, name })
            if (data) {
              tmp_id.push(data.id)
              tmp_name.push(data.name)
            }
          }
        }
      }
      newProperty.fields = tmp_id
      newProperty.fields_name = tmp_name
    } else if (
      ['value', 'prepend', 'append', 'increase', 'decrease'].includes(
        property.operation
      )
    ) {
      let tmp_id = ''
      let tmp_name = ''
      if (dataList.length > 0) {
        const data = this.getDataFromList(dataList, {
          id: property.field_id,
          name: property.field_name
        })
        if (data) {
          tmp_id = data.id
          tmp_name = data.name
        }
      }
      newProperty.field_id = tmp_id
      newProperty.field_name = tmp_name
    } else if (property.operation === 'lookup') {
      // field
      let tmp_id = ''
      let tmp_name = ''
      if (dataList.length > 0) {
        const data = this.getDataFromList(dataList, {
          id: property.field_id,
          name: property.field_name
        })
        if (data) {
          tmp_id = data.id
          tmp_name = data.name
        }
      }
      newProperty.field_id = tmp_id
      newProperty.field_name = tmp_name

      // lookup table
      let tmp_id_2 = ''
      let tmp_name_2 = ''
      const dataList_2 = this.dataList.lookupTables
      if (dataList_2.length > 0) {
        const data = this.getDataFromList(dataList_2, {
          id: property.lookup_table_id,
          name: property.lookup_table_name
        })
        if (data) {
          tmp_id_2 = data.id
          tmp_name_2 = data.name
        }
      }
      newProperty.lookup_table_id = tmp_id_2
      newProperty.lookup_table_name = tmp_name_2
    }

    return newProperty
  }

  remapObjectInput(property) {
    let newProperty = property

    let tmp_id = ''
    let tmp_name = ''
    const dataList = this.dataList.grammars
    if (dataList.length > 0 && property.config.default_input.asr) {
      const data = this.getDataFromList(dataList, {
        id:
          ((((property || {}).config || {}).default_input || {}).asr || {})
            .grammar_id || '',
        name:
          ((((property || {}).config || {}).default_input || {}).asr || {})
            .grammar_name || ''
      })
      if (data) {
        tmp_id = data.id
        tmp_name = data.name
      }
    }
    if (tmp_id) {
      newProperty.config.default_input.asr.grammar_id = tmp_id
      newProperty.config.default_input.asr.grammar_name = tmp_name
    }

    return newProperty
  }

  getDataFromList(dataList, { id, name }) {
    const data = _.find(
      dataList,
      item =>
        item.id === id || (name || '').toLowerCase() === item.name.toLowerCase()
    )
    return data
  }

  async initialDataList(platform = 'voice') {
    await this.getFlows(platform)
    await this.getQueues(platform)
    await this.getFields()
    await this.getIntents()
    await this.getGrammars()
    await this.getContacts()
    await this.getSchedules()
    await this.getLookupTables()
    await this.getTtsDictionaries()
  }

  async getFlows(platform) {
    try {
      const { data } = await apiFlow.listFlowUiByPlatform(platform)
      this.dataList.flows = []
      this.dataList.flows = data.map(item => ({
        _id: item._id,
        id: item._id,
        name: item.name
      }))
    } catch {}
  }

  async getQueues(platform) {
    try {
      const { data } =
        platform === 'chat'
          ? await apiQueue.getQueueChat()
          : await apiQueue.getQueueVoice()
      this.dataList.queues = []
      this.dataList.queues = data.map(item => ({
        _id: item._id,
        id: item._id,
        name: item.name
      }))
    } catch {}
  }

  async getFields() {
    try {
      const { data } = await apiField.activeField()
      this.dataList.fields = []
      this.dataList.fields = data.map(item => ({
        _id: item._id,
        id: item._id,
        name: `${item.group}.${item.name}`
      }))
    } catch {}
  }

  async getIntents() {
    const { data } = await apiIntent.getIntent()
    this.dataList.intents = []
    this.dataList.intents = data.map(item => ({
      _id: item._id,
      id: item._id,
      name: item.name
    }))
  }

  async getGrammars() {
    try {
      const { data } = await apiGrammar.getGrammar()
      this.dataList.grammars = []
      this.dataList.grammars = data.map(item => ({
        _id: item._id,
        id: item._id,
        name: item.name
      }))
    } catch {}
  }

  async getContacts() {
    try {
      const { data } = await apiContact.getContact()
      this.dataList.contacts = []
      this.dataList.contacts = data.map(item => ({
        _id: item._id,
        id: item._id,
        name: item.name,
        number: item.phone
      }))
    } catch {}
  }

  async getSchedules() {
    try {
      const { data } = await apiSchedule.getSchedules()
      this.dataList.schedules = []
      this.dataList.schedules = data.map(item => ({
        _id: item._id,
        id: item.schedule_id,
        name: item.name
      }))
    } catch {}
  }

  async getLookupTables() {
    try {
      const { data } = await apiLookupTables.getLookupTablesList()
      this.dataList.lookupTables = []
      this.dataList.lookupTables = data.map(item => ({
        _id: item._id,
        id: item.lookup_table_id,
        name: item.table_name
      }))
    } catch {}
  }

  async getTtsDictionaries() {
    try {
      const { data } = await apiTtsDictionaries.getTtsDictionaryList()
      this.dataList.ttsDictionaries = []
      this.dataList.ttsDictionaries = data.map(item => ({
        _id: item._id,
        id: item.tts_dictionary_id,
        name: item.name
      }))
    } catch {}
  }

  // end remap object of component

  // // start re-order id
  reorderNodeIdAndTab({ tabs, ui }) {
    let newTabs = JSON.parse(JSON.stringify(tabs))
    const drawflow = JSON.parse(JSON.stringify(ui.drawflow))

    const tempAllNodeId = {}
    const flowNodeIds = this.getDrawFlowNodeId(drawflow)
      .map(id => parseInt(id))
      .sort((a, b) => a - b)

    for (let i = 0; i < flowNodeIds.length; i++) {
      const oldId = flowNodeIds[i]
      const newId = i + 1
      tempAllNodeId[oldId] = newId
    }

    if (_.max(flowNodeIds) > 10000) {
      // tab
      newTabs = newTabs.map(item => {
        item.id = tempAllNodeId[item.id] || item.id
        return item
      })

      // ui
      const newJsonUi = {}
      for (const [page, obj] of Object.entries(drawflow)) {
        newJsonUi[page] = { data: {} }
        for (const [id, value] of Object.entries(obj.data)) {
          // id
          value.id = tempAllNodeId[value.id]

          // input
          if (Object.keys(value.inputs).length > 0) {
            for (const input in value.inputs) {
              value.inputs[input].connections = value.inputs[
                input
              ].connections.map(item => {
                return {
                  ...item,
                  node: tempAllNodeId[parseInt(item.node)]
                }
              })
            }
          }

          // output
          if (Object.keys(value.outputs).length > 0) {
            for (const output in value.outputs) {
              value.outputs[output].connections = value.outputs[
                output
              ].connections.map(item => {
                return {
                  ...item,
                  node: tempAllNodeId[parseInt(item.node)]
                }
              })
            }
          }

          // property
          if (value.data && value.data.property) {
            if (
              value.data.property.page &&
              tempAllNodeId[parseInt(value.data.property.page)]
            )
              value.data.property.page =
                tempAllNodeId[parseInt(value.data.property.page)]
            if (
              value.data.property.component &&
              tempAllNodeId[parseInt(value.data.property.component)]
            )
              value.data.property.component =
                tempAllNodeId[parseInt(value.data.property.component)]
          }

          newJsonUi[page].data[tempAllNodeId[id]] = value
        }
      }

      ui.drawflow = newJsonUi
    }

    return { newTab: newTabs, newUi: ui }
  }
  // reorderNodeId(ui) {
  //   this.tempAllNodeId = []
  //   const drawflow = ui.drawflow
  //   const flowNodeIds = this.getDrawFlowNodeId(drawflow)
  //     .map(id => parseInt(id))
  //     .sort((a, b) => a - b)
  //   for (let index = flowNodeIds.length - 1; index >= 0; index--) {
  //     const newId = index + 1
  //     const oldId = flowNodeIds[index]
  //     const gap = newId - oldId
  //     this.tempAllNodeId.push({ newId, oldId, gap })
  //   }
  //   ui.drawflow = this.reComponentIdAllPage(drawflow)
  //   return ui
  // }

  // reComponentIdAllPage(drawflow) {
  //   for (const page of Object.keys(drawflow)) {
  //     const template = this.reComponentId(drawflow[page].data)
  //     drawflow[page].data = template
  //   }
  //   return drawflow
  // }

  // reComponentId(template) {
  //   let newJsonUi = {}

  //   for (const [key, value] of Object.entries(template)) {
  //     let newValue = value
  //     newValue.id +=
  //       (this.tempAllNodeId.find(obj => obj.oldId === newValue.id) || {}).gap ||
  //       0

  //     // input
  //     if (Object.keys(value.inputs).length > 0) {
  //       for (const input in value.inputs) {
  //         newValue.inputs[input].connections = value.inputs[
  //           input
  //         ].connections.map(item => {
  //           const gap =
  //             (
  //               this.tempAllNodeId.find(
  //                 obj => obj.oldId === parseInt(item.node)
  //               ) || {}
  //             ).gap || 0
  //           return {
  //             ...item,
  //             node: `${parseInt(item.node) + gap}`
  //           }
  //         })
  //       }
  //     }

  //     // output
  //     if (Object.keys(value.outputs).length > 0) {
  //       for (const output in value.outputs) {
  //         newValue.outputs[output].connections = value.outputs[
  //           output
  //         ].connections.map(item => {
  //           const gap =
  //             (
  //               this.tempAllNodeId.find(
  //                 obj => obj.oldId === parseInt(item.node)
  //               ) || {}
  //             ).gap || 0
  //           return {
  //             ...item,
  //             node: `${parseInt(item.node) + gap}`
  //           }
  //         })
  //       }
  //     }

  //     newJsonUi[
  //       parseInt(key) +
  //         this.tempAllNodeId.find(item => item.oldId === parseInt(key)).gap
  //     ] = newValue
  //   }

  //   newJsonUi = this.reIdComponentProperty(newJsonUi)

  //   return newJsonUi
  // }

  // reIdComponentProperty(newJsonUi) {
  //   for (const [key, value] of Object.entries(newJsonUi)) {
  //     if (
  //       value.data.node === 'jump' &&
  //       value.data.property.type === 'component'
  //     ) {
  //       const oldComponentId = value.data.property.component
  //       const gap =
  //         (
  //           this.tempAllNodeId.find(
  //             obj => obj.oldId === parseInt(oldComponentId)
  //           ) || {}
  //         ).gap || 0
  //       value.data.property.component = `${parseInt(oldComponentId) + gap}`
  //     }

  //     if (value.data.node === 'page') {
  //       if (value.data.property.is_custom_component) {
  //         const oldComponentId = value.data.property.component
  //         const gap =
  //           (
  //             this.tempAllNodeId.find(
  //               obj => obj.oldId === parseInt(oldComponentId)
  //             ) || {}
  //           ).gap || 0
  //         value.data.property.component = `${parseInt(oldComponentId) + gap}`
  //       } else {
  //         const oldPageId = value.data.property.page
  //         const gap =
  //           (
  //             this.tempAllNodeId.find(
  //               obj => obj.oldId === parseInt(oldPageId)
  //             ) || {}
  //           ).gap || 0
  //         value.data.property.page = `${parseInt(oldPageId) + gap}`
  //       }
  //     }

  //     if (value.data.node === 'intent_mapping') {
  //       value.data.property.mapping.map(item => {
  //         const gap =
  //           (
  //             this.tempAllNodeId.find(
  //               obj => obj.oldId === parseInt(item.page)
  //             ) || {}
  //           ).gap || 0
  //         return {
  //           ...item,
  //           page: `${parseInt(item.page) + gap}`
  //         }
  //       })
  //     }
  //   }

  //   return newJsonUi
  // }
  // // end re-order id

  // // start page start
  // findPageMinId(obj) {
  //   const arr = _.values(obj.data)
  //   const minObj = _.find(arr, o => {
  //     return o.name === 'start' || o.name === 'enter'
  //   })
  //   return parseInt(minObj.id)
  // }
  // findPageId(tabs, page) {
  //   const pageObj = _.find(tabs, { name: page })
  //   return parseInt(pageObj.id)
  // }
  // // end page start
}

export default Arrange
